import type { CreatePatientByStaffParams } from "../types/params";

export type PatientRegistrationValues = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  government_id: string;
  government_id_type: "national_id" | "iqama" | "";
  hospital_tenant_code: string;
  date_of_birth: string;
  password: string;
  password_confirm: string;
};

export type PatientRegistrationErrors = Partial<
  Record<keyof PatientRegistrationValues, string>
>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validatePatientRegistrationValues = (
  values: PatientRegistrationValues
): PatientRegistrationErrors => {
  const nextErrors: PatientRegistrationErrors = {};

  if (!values.first_name.trim()) {
    nextErrors.first_name = "First name is required";
  }
  if (!values.last_name.trim()) {
    nextErrors.last_name = "Last name is required";
  }
  if (!values.email.trim()) {
    nextErrors.email = "Email is required";
  } else if (!EMAIL_REGEX.test(values.email.trim())) {
    nextErrors.email = "Please enter a valid email";
  }
  if (!values.hospital_tenant_code) {
    nextErrors.hospital_tenant_code = "Hospital is required";
  }
  if (!values.government_id.trim()) {
    nextErrors.government_id = "Government ID is required";
  } else if (!/^\d{10}$/.test(values.government_id.trim())) {
    nextErrors.government_id = "Government ID must be exactly 10 digits";
  }
  if (!values.government_id_type) {
    nextErrors.government_id_type = "Government ID type is required";
  }
  if (!values.password) {
    nextErrors.password = "Password is required";
  } else if (values.password.length < 4) {
    nextErrors.password = "Password must be at least 4 characters";
  }
  if (!values.password_confirm) {
    nextErrors.password_confirm = "Confirm password is required";
  } else if (values.password !== values.password_confirm) {
    nextErrors.password_confirm = "Passwords do not match";
  }

  return nextErrors;
};

export const buildCreatePatientPayload = (
  values: PatientRegistrationValues
): CreatePatientByStaffParams => {
  return {
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    email: values.email.trim(),
    phone_number: values.phone_number.trim() || undefined,
    government_id: values.government_id.trim(),
    government_id_type: values.government_id_type as "national_id" | "iqama",
    tenant_code: values.hospital_tenant_code,
    date_of_birth: values.date_of_birth
      ? new Date(values.date_of_birth.replace(/\//g, "-")).toISOString()
      : undefined,
    password: values.password,
    password_confirm: values.password_confirm,
  };
};

export const mapPatientRegistrationApiError = (error: unknown): string => {
  const err = error as {
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const detail = err?.response?.data?.detail;
  const status = err?.response?.status;

  if (status === 409 && typeof detail === "string") {
    return detail;
  }
  if (status === 422 && typeof detail === "string") {
    return detail;
  }
  if ((status === 401 || status === 403) && typeof detail === "string") {
    return detail;
  }
  return "Failed to register patient. Please try again.";
};
